import { motion } from "framer-motion";
import {
  Factory,
  FileCheck2,
  Ruler,
  Timer,
  PackageCheck,
  Handshake,
} from "lucide-react";

const REASONS = [
  {
    icon: Factory,
    title: "In-house manufacturing",
    body: "Cutting, forging, CNC turning, thread rolling and finishing all happen under one roof at Makarpura GIDC — so quality and delivery stay in our control, not a sub-contractor's.",
  },
  {
    icon: Ruler,
    title: "Made to your drawing",
    body: "Non-standard heads, stepped studs, special lengths, tie rods and clamps are routine work for us. Send a drawing, sketch or even a sample and we will reproduce it.",
  },
  {
    icon: FileCheck2,
    title: "Documented quality",
    body: "ISO 9001:2015 certified system with mill test certificates, batch traceability, hardness and dimensional checks — and third-party inspection whenever your PO calls for it.",
  },
  {
    icon: Timer,
    title: "Shutdown-ready response",
    body: "Standard items move in 3–7 working days and urgent plant shutdown requirements get priority scheduling, because a stalled line costs far more than a fastener.",
  },
  {
    icon: PackageCheck,
    title: "Full grade coverage",
    body: "High tensile, alloy, carbon, mild steel and stainless 304 / 316 / 321 plus duplex and special alloys — one vendor for the entire bolting scope of your project.",
  },
  {
    icon: Handshake,
    title: "Direct with the makers",
    body: "You speak to the people who run the machines, not a call centre. Nearly two decades of repeat business has been built on exactly that.",
  },
];

const FADE_UP = { opacity: 0, y: 22 };
const FADE_IN = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, amount: 0.2 };
const CARD_HOVER = { y: -4 };
const HEAD_HIDDEN = { opacity: 0, y: 20 };
const HEAD_VIEWPORT = { once: true, amount: 0.3 };
const HEAD_TRANSITION = { duration: 0.6 };

export default function WhyUs() {
  return (
    <section id="why-us" className="bg-mist py-20 lg:py-28" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={HEAD_HIDDEN}
          whileInView={FADE_IN}
          viewport={HEAD_VIEWPORT}
          transition={HEAD_TRANSITION}
          className="max-w-2xl mb-12"
        >
          <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.12em] uppercase text-steel font-semibold mb-4">
            <span className="w-10 h-[2px] bg-brand" />
            Why Rahil Engineering Works
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl text-ink">
            Six reasons buyers
            <br />
            keep reordering
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {REASONS.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.article
                key={r.title}
                initial={FADE_UP}
                whileInView={FADE_IN}
                viewport={CARD_VIEWPORT}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                whileHover={CARD_HOVER}
                onMouseMove={(e) => {
                  const b = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - b.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - b.top}px`);
                }}
                className="spotlight-card group relative overflow-hidden bg-white border border-steel-light rounded-2xl p-6 lg:p-7 hover:border-brand hover:shadow-xl transition-all duration-300"
                data-testid={`why-card-${i + 1}`}
              >
                <span className="absolute top-5 right-6 font-display font-black text-5xl text-steel-light/70 leading-none select-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="relative inline-flex w-12 h-12 rounded-xl bg-navy-deep text-white items-center justify-center mb-5 group-hover:bg-brand group-hover:-translate-y-0.5 transition-all duration-500">
                  <Icon size={20} />
                </span>
                <h3 className="relative font-display font-bold uppercase text-base lg:text-lg text-ink leading-snug mb-2.5">
                  {r.title}
                </h3>
                <p className="relative text-sm text-slate-600 leading-relaxed">{r.body}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
